import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { ApiException } from '../api/client';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { AccentCard } from '../components/Card';
import { Input } from '../components/Input';
import { AuthLayout } from '../components/Layout';

interface LocationState {
  kodeKlien?: string;
  email?: string;
}

export function CompleteProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? {}) as LocationState;

  const [kodeKlien, setKodeKlien] = useState(state.kodeKlien ?? '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [namaLengkap, setNamaLengkap] = useState('');
  const [nomorHp, setNomorHp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Password dan konfirmasi password tidak cocok.');
      return;
    }

    setLoading(true);
    try {
      const result = await authApi.completeProfile({
        kodeKlien: kodeKlien.trim(),
        username: username.trim().toLowerCase(),
        password,
        namaLengkap: namaLengkap.trim(),
        nomorHp: nomorHp.trim() || undefined,
      });
      setSuccess(result.message);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Terjadi kesalahan tak terduga.');
    } finally {
      setLoading(false);
    }
  };

  if (!kodeKlien) {
    return (
      <AuthLayout>
        <AccentCard title="Lengkapi Profil">
          <Alert variant="warning" title="Kode Klien tidak ditemukan">
            Halaman ini membutuhkan Kode Klien dari link verifikasi email.
          </Alert>
          <div className="mt-5 space-y-2">
            <Link to="/register" className="block">
              <Button variant="primary" fullWidth>
                Daftar Akun
              </Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="outline" fullWidth>
                Login
              </Button>
            </Link>
          </div>
        </AccentCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AccentCard title="Lengkapi Profil" subtitle="Langkah 3 dari 3">
        {success && (
          <div className="mb-4">
            <Alert variant="success" title="Profil berhasil dilengkapi">
              {success}
              <div className="mt-1 text-xs">Anda akan diarahkan ke halaman login...</div>
            </Alert>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Kode Klien"
            value={kodeKlien}
            onChange={(e) => setKodeKlien(e.target.value)}
            disabled
            hint="Auto-generated oleh sistem."
          />
          {state.email && (
            <Input label="Email" value={state.email} disabled />
          )}
          <Input
            label="Username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="huruf kecil, angka, underscore (min 4)"
            required
            autoComplete="username"
            disabled={loading || !!success}
            hint="Tidak bisa diubah setelah pendaftaran."
          />
          <Input
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimal 8 karakter"
            required
            autoComplete="new-password"
            disabled={loading || !!success}
          />
          <Input
            label="Konfirmasi Password"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={loading || !!success}
          />
          <Input
            label="Nama Lengkap"
            name="namaLengkap"
            value={namaLengkap}
            onChange={(e) => setNamaLengkap(e.target.value)}
            required
            disabled={loading || !!success}
          />
          <Input
            label="Nomor HP (opsional)"
            name="nomorHp"
            type="tel"
            value={nomorHp}
            onChange={(e) => setNomorHp(e.target.value)}
            placeholder="08xxxxxxxxxx"
            disabled={loading || !!success}
          />
          <Button type="submit" variant="primary" fullWidth loading={loading} disabled={!!success}>
            Selesaikan Pendaftaran
          </Button>
        </form>
      </AccentCard>
    </AuthLayout>
  );
}
